import type { ReportCatalogStatus, ReportDeliveryMode } from '../../services/types';

export type ReportModule = 'Sales' | 'Estimation' | 'Quotation' | 'Production' | 'Inventory' | 'Financials' | 'Owner';
export type ReportPreviewKind = 'table' | 'summary';

export interface ReportDefinition {
    id: string;
    title: string;
    module: ReportModule;
    status: ReportCatalogStatus;
    deliveryMode: ReportDeliveryMode;
    readinessKey?: string;
    description: string;
    previewKind: ReportPreviewKind;
    jsonEndpoint?: string;
    exportEndpoint?: string;
    targetRoute?: string;
    supportsPagination: boolean;
    defaultPageSize: 25 | 50 | 100;
    filters: Array<'search' | 'dateRange' | 'groupBy' | 'asOfDate'>;
    exportable: boolean;
    blocker?: string;
}

export const reportDefinitions: ReportDefinition[] = [
    {
        id: 'enquiry-register',
        title: 'Enquiry Register',
        module: 'Sales',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Date, client, product type, derived estimate value, and enquiry status.',
        previewKind: 'table',
        jsonEndpoint: '/reports/enquiries/register',
        exportEndpoint: '/reports/enquiries/register/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search', 'dateRange'],
        exportable: true,
    },
    {
        id: 'sales-pipeline',
        title: 'Sales Pipeline Value',
        module: 'Sales',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Enquiry value, quotation value, and expected orders using quoted-not-converted estimates.',
        previewKind: 'table',
        jsonEndpoint: '/reports/sales/pipeline',
        exportEndpoint: '/reports/sales/pipeline/export.csv',
        supportsPagination: false,
        defaultPageSize: 25,
        filters: ['dateRange'],
        exportable: true,
    },
    {
        id: 'conversion-ratio',
        title: 'Conversion Ratio Report',
        module: 'Sales',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Period counts of enquiries, quotations, orders won, and conversion percentage.',
        previewKind: 'table',
        jsonEndpoint: '/reports/enquiries/conversion',
        exportEndpoint: '/reports/enquiries/conversion/export.csv',
        supportsPagination: false,
        defaultPageSize: 25,
        filters: ['dateRange'],
        exportable: true,
    },
    {
        id: 'estimated-cost-breakdown',
        title: 'Estimated Cost Breakdown',
        module: 'Estimation',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Estimate-side material, labour, BOM, installation, overhead, transport, and margin.',
        previewKind: 'table',
        jsonEndpoint: '/reports/estimates/cost-breakdown',
        exportEndpoint: '/reports/estimates/cost-breakdown/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search', 'dateRange'],
        exportable: true,
    },
    {
        id: 'estimated-vs-actual',
        title: 'Estimated vs Actual Cost',
        module: 'Estimation',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Estimate total cost versus actual production cost per job.',
        previewKind: 'table',
        jsonEndpoint: '/reports/jobs/estimated-vs-actual',
        exportEndpoint: '/reports/jobs/estimated-vs-actual/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search'],
        exportable: true,
    },
    {
        id: 'quotation-register',
        title: 'Quotation Register',
        module: 'Quotation',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Estimate-driven quotation number, client, value, and status.',
        previewKind: 'table',
        jsonEndpoint: '/reports/quotations/register',
        exportEndpoint: '/reports/quotations/register/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search', 'dateRange'],
        exportable: true,
    },
    {
        id: 'wip-report',
        title: 'WIP Report',
        module: 'Production',
        status: 'BLOCKED',
        deliveryMode: 'blocked',
        readinessKey: 'wip-report',
        description: 'Work-in-progress completion and value.',
        previewKind: 'table',
        jsonEndpoint: '/reports/production/wip',
        exportEndpoint: '/reports/production/wip/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['dateRange'],
        exportable: true,
        blocker: 'WIP value remains blocked until the valuation rule is approved.',
    },
    {
        id: 'production-output',
        title: 'Production Output Report',
        module: 'Production',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Daily or weekly output quantities and recorded output weight.',
        previewKind: 'table',
        jsonEndpoint: '/reports/production/output',
        exportEndpoint: '/reports/production/output/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search', 'dateRange', 'groupBy'],
        exportable: true,
    },
    {
        id: 'job-production-cost',
        title: 'Job Production Cost Report',
        module: 'Production',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Job quantity, weight, material issued cost, split labour hours, and total production cost.',
        previewKind: 'table',
        jsonEndpoint: '/reports/production/job-costs',
        exportEndpoint: '/reports/production/job-costs/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search', 'dateRange'],
        exportable: true,
    },
    {
        id: 'material-consumption-per-job',
        title: 'Material Consumption Report',
        module: 'Production',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Issued item quantities and costs per work order.',
        previewKind: 'table',
        jsonEndpoint: '/reports/production/material-consumption',
        exportEndpoint: '/reports/production/material-consumption/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search', 'dateRange'],
        exportable: true,
    },
    {
        id: 'raw-material-stock',
        title: 'Inventory Valuation Report',
        module: 'Inventory',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Current stock valuation by item using weighted average cost.',
        previewKind: 'table',
        jsonEndpoint: '/reports/inventory/raw-material-stock',
        exportEndpoint: '/reports/inventory/raw-material-stock/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search'],
        exportable: true,
    },
    {
        id: 'minimum-stock-alert',
        title: 'Minimum Stock Alert',
        module: 'Inventory',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Low or depleted stock based on reorder threshold when available.',
        previewKind: 'table',
        jsonEndpoint: '/reports/inventory/min-stock-alert',
        exportEndpoint: '/reports/inventory/min-stock-alert/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search'],
        exportable: true,
    },
    {
        id: 'grc-material-consumption',
        title: 'GRC Material Consumption Per Job',
        module: 'Inventory',
        status: 'BLOCKED',
        deliveryMode: 'blocked',
        readinessKey: 'grc-material-consumption',
        description: 'Bucketed cement, sand, fiber, and additive usage by job.',
        previewKind: 'table',
        jsonEndpoint: '/reports/production/grc-material-consumption',
        exportEndpoint: '/reports/production/grc-material-consumption/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['dateRange'],
        exportable: true,
        blocker: 'Explicit item-to-GRC consumption bucket mapping is still not available.',
    },
    {
        id: 'stock-ledger',
        title: 'Stock Ledger',
        module: 'Inventory',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Historical stock movements with quantity, rate, valuation, source, and reference traceability.',
        previewKind: 'table',
        jsonEndpoint: '/reports/inventory/stock-ledger',
        exportEndpoint: '/reports/inventory/stock-ledger/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search', 'dateRange'],
        exportable: true,
    },
    {
        id: 'trial-balance',
        title: 'Trial Balance',
        module: 'Financials',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Account-level trial balance with debit and credit totals as of the selected date.',
        previewKind: 'table',
        jsonEndpoint: '/reports/financials/trial-balance',
        exportEndpoint: '/reports/financials/trial-balance/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search', 'asOfDate'],
        exportable: true,
    },
    {
        id: 'cash-flow',
        title: 'Cash Flow Report',
        module: 'Financials',
        status: 'LIVE',
        deliveryMode: 'replicated',
        readinessKey: 'cash-flow',
        description: 'Opening balance, cash in, cash out, and closing balance based on the approved interim cash-account basis.',
        previewKind: 'table',
        jsonEndpoint: '/reports/financials/cash-flow',
        exportEndpoint: '/reports/financials/cash-flow/export.csv',
        supportsPagination: false,
        defaultPageSize: 25,
        filters: ['dateRange'],
        exportable: true,
    },
    {
        id: 'receivable-aging',
        title: 'Receivable Aging',
        module: 'Financials',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Outstanding receivables bucketed into 0-30, 31-60, 61-90, and 90+.',
        previewKind: 'table',
        jsonEndpoint: '/reports/financials/receivables-aging',
        exportEndpoint: '/reports/financials/receivables-aging/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search', 'asOfDate'],
        exportable: true,
    },
    {
        id: 'payable-aging',
        title: 'Payable Aging',
        module: 'Financials',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Supplier outstanding aging integrated from the finance settlement module.',
        previewKind: 'table',
        jsonEndpoint: '/reports/financials/payables-aging',
        exportEndpoint: '/reports/financials/payables-aging/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search', 'asOfDate'],
        exportable: true,
    },
    {
        id: 'customer-statement',
        title: 'Customer Statement',
        module: 'Financials',
        status: 'LIVE',
        deliveryMode: 'linked',
        description: 'Opens the current production customer statement workspace in Financials.',
        previewKind: 'summary',
        targetRoute: '/financials?tab=customer-statement',
        supportsPagination: false,
        defaultPageSize: 25,
        filters: [],
        exportable: false,
    },
    {
        id: 'supplier-statement',
        title: 'Supplier Statement',
        module: 'Financials',
        status: 'LIVE',
        deliveryMode: 'linked',
        description: 'Opens the current production supplier statement workspace in Financials.',
        previewKind: 'summary',
        targetRoute: '/financials?tab=supplier-statement',
        supportsPagination: false,
        defaultPageSize: 25,
        filters: [],
        exportable: false,
    },
    {
        id: 'vat-summary',
        title: 'VAT Summary',
        module: 'Financials',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Output VAT, input VAT, net VAT payable, and document-level VAT lines.',
        previewKind: 'table',
        jsonEndpoint: '/reports/vat-summary',
        exportEndpoint: '/reports/vat-summary/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search', 'dateRange'],
        exportable: true,
    },
    {
        id: 'balance-sheet',
        title: 'Balance Sheet',
        module: 'Financials',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Assets, liabilities, and equity by account, with section totals as of the selected date.',
        previewKind: 'table',
        jsonEndpoint: '/reports/financials/balance-sheet',
        exportEndpoint: '/reports/financials/balance-sheet/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['asOfDate'],
        exportable: true,
    },
    {
        id: 'profit-and-loss',
        title: 'Profit & Loss Statement',
        module: 'Financials',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Income and expense account breakdown with total income, total expense, and net profit.',
        previewKind: 'table',
        jsonEndpoint: '/reports/p-and-l',
        exportEndpoint: '/reports/p-and-l/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['dateRange'],
        exportable: true,
    },
    {
        id: 'break-even',
        title: 'Break-Even Analysis',
        module: 'Financials',
        status: 'BLOCKED',
        deliveryMode: 'blocked',
        readinessKey: 'break-even',
        description: 'Minimum sales required to cover fixed cost base.',
        previewKind: 'summary',
        jsonEndpoint: '/reports/financials/break-even',
        exportEndpoint: '/reports/financials/break-even/export.csv',
        supportsPagination: false,
        defaultPageSize: 25,
        filters: ['dateRange'],
        exportable: true,
        blocker: 'Break-even remains blocked until fixed-vs-variable accounting policy is approved.',
    },
    {
        id: 'owner-dashboard',
        title: 'Owner Dashboard',
        module: 'Owner',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Sales MTD, receivables, payables, bank balance, production cost to date, and explicit GP basis.',
        previewKind: 'table',
        jsonEndpoint: '/reports/owner/dashboard',
        exportEndpoint: '/reports/owner/dashboard/export.csv',
        supportsPagination: false,
        defaultPageSize: 25,
        filters: [],
        exportable: true,
    },
    {
        id: 'product-profitability',
        title: 'Product Profitability Report',
        module: 'Owner',
        status: 'BLOCKED',
        deliveryMode: 'blocked',
        readinessKey: 'product-profitability',
        description: 'Product-family sales, cost, and profit percentage.',
        previewKind: 'table',
        jsonEndpoint: '/reports/owner/product-profitability',
        exportEndpoint: '/reports/owner/product-profitability/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['dateRange'],
        exportable: true,
        blocker: 'Product-family profitability stays blocked until the family rollup basis is validated across estimate, production, and invoice data.',
    },
    {
        id: 'contribution-margin-per-job',
        title: 'Contribution Margin Per Job',
        module: 'Owner',
        status: 'LIVE',
        deliveryMode: 'replicated',
        description: 'Sales versus direct production cost excluding overhead.',
        previewKind: 'table',
        jsonEndpoint: '/reports/jobs/contribution-margin',
        exportEndpoint: '/reports/jobs/contribution-margin/export.csv',
        supportsPagination: true,
        defaultPageSize: 25,
        filters: ['search'],
        exportable: true,
    },
];

export const reportModules: ReportModule[] = ['Sales', 'Estimation', 'Quotation', 'Production', 'Inventory', 'Financials', 'Owner'];
